import '../../styles/InteractiveComponents.css';

import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
  ResponderProvided
} from 'react-beautiful-dnd';
import {
  LessonComponentResponseType,
  SequencerComponent
} from '../../models/LessonComponent';
import React, { useEffect, useState } from 'react';
import { cloneDeep, shuffle } from 'lodash';

import DeleteIcon from '@mui/icons-material/Delete';
import Grid from '@mui/material/Unstable_Grid2';
import { IconButtonWithConfirmation } from '../../../shared/components/IconButtonWithConfirmation';
import { InlineTextEdit } from '../InlineTextEdit';
import LessonResponseService from '../../services/LessonResponseService';
import { SequencerItem } from '../../models/LessonComponentContent';
import { SequencerSelectionItem } from './SequencerItem';
import { nanoid } from 'nanoid';
import styles from '../../styles/Sequencer.module.css';
import { useLessonContext } from '../../models/LessonContext';
import { useLessonEditContext } from '../../models/LessonEditContext';

interface SequencerSelectionProps {
  component: SequencerComponent;
  onChange: (response: LessonComponentResponseType) => void;
}

type LabelType = 'top' | 'bottom';

/** Maximum number of items allowed in the sequencer interaction. */
const MAX_SEQUENCER_ITEMS = 6;

/** Placeholder item displayed during edit mode to add new items. */
const blankSequencerItem: SequencerItem = { id: null, label: '', image: null };

export const SequencerSelection = (props: SequencerSelectionProps) => {
  const { mode } = useLessonContext();
  const { handleSettingsChange } = useLessonEditContext();
  const { items } = props.component.content.sequencer;
  const [currentItems, setCurrentItems] = useState<SequencerItem[]>(
    items || []
  );

  useEffect(() => {
    if (items) {
      setCurrentItems(items);
    }
  }, [items]);

  /** On first render, create a scrambled order and set as currentItems. */
  useEffect(() => {
    if (mode !== 'edit') {
      const shuffledItems = getShuffledItems(currentItems);
      setCurrentItems(shuffledItems);
    }
  }, [mode]);

  const getShuffledItems = (items: SequencerItem[]) => {
    let shuffledItems: SequencerItem[];
    // Checking to ensure the sequencer never starts in the correct order.
    for (let iter = 10; iter >= 0; iter -= 1) {
      shuffledItems = shuffle(items);
      if (shuffledItems.some((item, index) => item.id !== items[index].id)) {
        break;
      }
    }
    return shuffledItems;
  };

  /** Build array of items in the order chosen by the user. */
  if (props.component.answer && mode !== 'edit') {
    const userAnswer: string[] = JSON.parse(props.component.answer);
    currentItems.sort(
      (a, b) => userAnswer.indexOf(a.id) - userAnswer.indexOf(b.id)
    );
  }

  const getItemsIdArray = (items: SequencerItem[]): string[] => {
    const itemIdsArray = items.map((item) => item.id);
    return itemIdsArray;
  };

  const handleSaveLabel = (labelValue: string, type: string) => {
    const copy = cloneDeep(props.component);
    if (type === 'top') {
      copy.content.sequencer.topLabel = labelValue;
    } else {
      copy.content.sequencer.bottomLabel = labelValue;
    }
    handleSettingsChange(copy);
  };

  const saveResponsePattern = (sequencerComp: SequencerComponent) => {
    const res = LessonResponseService.getResponsePattern(
      props.component.type,
      getItemsIdArray(sequencerComp.content.sequencer.items)
    );
    sequencerComp.content.correctResponsePattern = res;
    setCurrentItems(sequencerComp.content.sequencer.items);
    handleSettingsChange(sequencerComp);
  };

  const onItemChange = (item: SequencerItem) => {
    const copy = cloneDeep(props.component);
    const index = copy.content.sequencer.items.findIndex(
      (i) => i.id === item.id
    );
    if (index < 0) {
      // New item
      if (item.id === null) {
        item.id = nanoid();
      }
      copy.content.sequencer.items.push(item);
    } else {
      copy.content.sequencer.items[index] = item;
    }
    saveResponsePattern(copy);
  };

  /** Delete the given item. */
  const onItemDelete = (id: string) => {
    const copy = cloneDeep(props.component);
    copy.content.sequencer.items = copy.content.sequencer.items.filter(
      (i) => i.id !== id
    );
    saveResponsePattern(copy);
  };

  /** Reorder the sequencer items. */
  const reorderOptions = (fromIndex: number, toIndex: number) => {
    if (mode === 'edit') {
      const compCopy = cloneDeep(props.component);
      const items = cloneDeep(currentItems);
      const movingItem = items[fromIndex];
      items.splice(fromIndex, 1);
      items.splice(toIndex, 0, movingItem);
      compCopy.content.sequencer.items = items;
      saveResponsePattern(compCopy);
    } else {
      const copy: string[] = getItemsIdArray(currentItems);
      const movingItem = copy[fromIndex];
      copy.splice(fromIndex, 1);
      copy.splice(toIndex, 0, movingItem);
      props.onChange(copy);
    }
  };

  const handleDragEnd = (result: DropResult, provided: ResponderProvided) => {
    // prevent from destination index outbound error
    if (!result.destination) return;
    reorderOptions(result.source.index, result.destination.index);
  };

  const getDroppableStyle = (isDraggingOver: boolean) => ({
    background: isDraggingOver ? '#eee' : 'transparent',
    height: '100%'
  });

  const sequencerComponentLabel = (label: LabelType) => {
    const labelValue =
      label === 'top'
        ? props.component.content.sequencer?.topLabel
        : props.component.content.sequencer?.bottomLabel;
    const placeholder =
      label === 'top'
        ? 'Click to edit upper label'
        : 'Click to edit lower label';
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        className={
          mode === 'edit'
            ? styles.sequencerLabelEdit
            : styles.sequencerLabelLearner
        }
      >
        {mode === 'edit' ? (
          <InlineTextEdit
            value={labelValue || ''}
            style={{ display: 'block' }}
            placeholder={placeholder}
            onChange={(value) => handleSaveLabel(value, label)}
          />
        ) : (
          <span>{labelValue}</span>
        )}
      </Grid>
    );
  };

  const draggableSequence = (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="selection-options">
        {(provided1, snapshot1) => (
          <div
            ref={provided1.innerRef}
            style={{
              ...getDroppableStyle(snapshot1.isDraggingOver),
              width: '100%'
            }}
          >
            {currentItems.map((item, index) => (
              <Draggable key={item.id} draggableId={item.id} index={index}>
                {(provided2, snapshot2) => (
                  <div ref={provided2.innerRef} {...provided2.draggableProps}>
                    <Grid container justifyContent="center">
                      <Grid className={styles.sequencerItemProperty}>
                        <SequencerSelectionItem
                          index={index}
                          provided2={provided2}
                          item={item}
                          onItemChange={onItemChange}
                          component={props.component}
                        />
                        <Grid>
                          {mode === 'edit' && (
                            <IconButtonWithConfirmation
                              size="small"
                              onConfirm={() => onItemDelete(item?.id)}
                            >
                              <DeleteIcon />
                            </IconButtonWithConfirmation>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </div>
                )}
              </Draggable>
            ))}
            {provided1.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );

  return (
    <Grid container direction="column">
      <Grid container justifyContent="center">
        {sequencerComponentLabel('top')}
      </Grid>
      <Grid container alignItems="center" direction="column">
        <Grid sx={{ width: '100%' }}>{draggableSequence}</Grid>
        {mode === 'edit' && currentItems.length < MAX_SEQUENCER_ITEMS && (
          <Grid
            container
            className={styles.blankItem}
            justifyContent="center"
            sx={{ width: '100%' }}
          >
            <div className={styles.sequencerItemProperty}>
              <SequencerSelectionItem
                item={blankSequencerItem}
                onItemChange={onItemChange}
                component={props.component}
              />
            </div>
          </Grid>
        )}
      </Grid>
      <Grid container justifyContent="center">
        {sequencerComponentLabel('bottom')}
      </Grid>
    </Grid>
  );
};
